<template>
    <div class="animated fadeIn">
        <b-card>
            <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0"><i class="fa fa-fw fa-search mr-2" />Daftar Temuan Inventaris</h5>
                <b-button v-if="canBulkComplete" variant="success" @click="$bvModal.show('m-bulk-approve-temuan')">
                    <i class="fa fa-fw fa-check mr-1" />Approve Sekaligus ke Inventory
                </b-button>
            </div>
            <hr />
            <b-row>
                <b-col md="2">
                    <b-form-group label="No. Faktur">
                        <b-form-input
                            v-model="filter.no_faktur"
                            maxlength="50"
                            placeholder="Cari No. Faktur..."
                            @keydown.enter="getItems()"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="IMEI / SN">
                        <b-form-input
                            v-model="filter.imei_sn"
                            maxlength="100"
                            placeholder="Cari IMEI / SN..."
                            @keydown.enter="getItems()"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="Status Temuan">
                        <b-form-select v-model="filter.status" :options="optStatus" />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="Sumber">
                        <b-form-select v-model="filter.origin_type" :options="optOrigin" />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="Divisi">
                        <b-form-select v-model="filter.division" :options="optDivision" />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="Aksi">
                        <b-button variant="primary" @click="getItems()">
                            <i class="fa fa-search mr-2" />Cari
                        </b-button>
                        <b-button variant="danger" class="ml-2" @click="resetFilter">Reset</b-button>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-table bordered hover show-empty responsive :busy="isBusy" :items="items" :fields="fields">
                <template v-slot:empty>
                    Tidak ada temuan inventaris.
                </template>
                <template v-slot:cell(imei_sn)="row">
                    {{ row.item.imei_sn || "-" }}
                </template>
                <template v-slot:cell(item_type)="row">
                    {{ row.item.item_type || "-" }}
                </template>
                <template v-slot:cell(origin_type)="row">
                    {{ originLabels[row.item.origin_type] || "-" }}
                </template>
                <template v-slot:cell(category)="row">
                    {{ row.item.category || "-" }}
                </template>
                <template v-slot:cell(status)="row">
                    <b-badge :variant="getVarianceStatusBadge(row.item.status).variant">
                        {{ getVarianceStatusBadge(row.item.status).text }}
                    </b-badge>
                    <div v-if="row.item.inventory_status === INVENTORY_STATUS.REQUEST_BUYBACK_PGI">
                        <b-badge variant="dark" class="mt-1">Request Kirim Balik PGI</b-badge>
                    </div>
                </template>
                <template v-slot:cell(created_by)="row">
                    {{ row.item.created_by || "-" }}
                </template>
                <template v-slot:cell(created_at)="row">
                    {{ $helper.parseDate(row.item.created_at) }}
                    {{ $helper.parseHourMinute(row.item.created_at) }}
                </template>
                <template v-slot:cell(action)="row">
                    <router-link :to="'/inventory-temuan/' + row.item.id">
                        <b-button size="sm" variant="primary" v-b-tooltip.hover.top title="Detail Temuan">
                            <i class="fa fa-fw fa-info" />
                        </b-button>
                    </router-link>
                </template>
            </b-table>
            <PageSizer
                v-if="loaded"
                :totalRows="totalRows"
                :page="currentPage"
                :limit="perPage"
                :options="options"
                @limit-change="onLimitChange"
                @page-change="onPageChange"
            />
        </b-card>
        <ModalBulkApproveTemuan @saved="getItems(currentPage)" />
    </div>
</template>

<script>
import EnumPermission from "@/shared/enum-permissions";
import ModalBulkApproveTemuan from "./ModalBulkApproveTemuan.vue";
import {
    getVarianceStatusBadge,
    VARIANCE_STATUS_OPTIONS,
    ORIGIN_OPTIONS,
    ORIGIN_LABELS,
    INVENTORY_STATUS
} from "@/shared/inventory-variance";

const FILTER_KEY = "listTemuanInventoryFilter";

function emptyFilter() {
    return { no_faktur: "", imei_sn: "", status: null, origin_type: null, division: null };
}

export default {
    components: { ModalBulkApproveTemuan },

    data() {
        return {
            isBusy: false,
            items: [],
            filter: emptyFilter(),
            fields: [
                { key: "no_faktur", label: "No Faktur" },
                { key: "imei_sn", label: "IMEI / SN" },
                { key: "item_type", label: "Tipe Barang" },
                { key: "origin_type", label: "Sumber" },
                { key: "category", label: "Kategori" },
                { key: "status", label: "Status", thClass: "text-center", tdClass: "text-center" },
                { key: "created_by", label: "Dibuat Oleh" },
                { key: "created_at", label: "Tanggal" },
                { key: "action", label: "Aksi" }
            ],
            options: [10, 20, 30, 40, 50].map(value => ({ value, text: String(value) })),
            optStatus: VARIANCE_STATUS_OPTIONS,
            optOrigin: ORIGIN_OPTIONS,
            optDivision: [{ value: null, text: "Semua Divisi" }],
            originLabels: ORIGIN_LABELS,
            INVENTORY_STATUS,
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            loaded: false
        };
    },

    computed: {
        canBulkComplete() {
            return this.$helper.isAllowed(EnumPermission.INVENTORY_VARIANCE_BULK_COMPLETE.toString());
        }
    },

    methods: {
        getVarianceStatusBadge,

        getItems(page = 1) {
            this.isBusy = true;
            this.currentPage = page;

            let param = this.$helper.getBasePagedQuery(this.currentPage, this.perPage);
            if (this.filter.no_faktur) param += `&filterNoFaktur=${encodeURIComponent(this.filter.no_faktur)}`;
            if (this.filter.imei_sn) param += `&filterImeiSn=${encodeURIComponent(this.filter.imei_sn)}`;
            if (this.filter.status) param += `&filterStatus=${this.filter.status}`;
            if (this.filter.origin_type) param += `&filterOriginType=${this.filter.origin_type}`;
            if (this.filter.division) param += `&filterDivision=${this.filter.division}`;

            return this.$axios
                .get("inventory-variances" + param)
                .then(res => {
                    this.items = res.data.data.items;
                    this.totalRows = res.data.data.total;
                    localStorage.setItem(
                        FILTER_KEY,
                        JSON.stringify({ ...this.filter, page: this.currentPage, perPage: this.perPage })
                    );
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => {
                    this.isBusy = false;
                    this.loaded = true;
                });
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getItems(this.currentPage);
        },

        onPageChange(newPage) {
            this.getItems(newPage);
        },

        getDivisions() {
            const divisions = [{ value: 1, text: "HP" }, { value: 2, text: "NON HP" }];

            return this.$axios
                .get("division/get-all-filtered")
                .then(res => {
                    this.optDivision = [
                        this.optDivision[0],
                        ...divisions.filter(division => res.data.data.some(item => item.id === division.value))
                    ];
                })
                .catch(error => this.$helper.parseError(this, error));
        },

        resetFilter() {
            this.filter = emptyFilter();
            this.getItems();
        }
    },

    created() {
        let page = 1;
        if (!this.$helper.isBlank(localStorage.getItem(FILTER_KEY))) {
            const { page: savedPage, perPage, ...filter } = JSON.parse(localStorage.getItem(FILTER_KEY));
            this.filter = Object.assign(emptyFilter(), filter);
            if (perPage) this.perPage = perPage;
            if (savedPage) page = savedPage;
        }
        this.getItems(page);
        this.getDivisions();
    }
};
</script>
